import { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

import { CardShell } from '@shared/ui/CardShell';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Skeleton } from '@shared/ui/Skeleton';
import {
  useGetComposedDocQuery,
  useDeleteComposedMutation,
  useListAuditSessionsQuery,
} from '@shared/api';
import { downloadAuthorizedFile } from '@shared/lib/downloadFile';
import { useAppSelector } from '@app/hooks';
import { useAgentPermission } from '@entities/user';
import {
  KIND_BADGE,
  composedKindLabel,
  formatComposedDt,
  formatSize,
} from '@entities/legal/composed';
import type { AuditSession } from '@entities/legal/types';
import { ExternalAuditModal } from './ExternalAuditModal';

/**
 * Единая страница одного сгенерированного DOCX
 * (`/agents/statut/documents/:composedId`). Доступна из четырёх мест:
 * архива (ComposedDocs), карточки договора (протоколы разногласий), карточки
 * претензии (ответы / перевыставления) и карточки иска. Показывает
 * метаданные документа, ссылку на источник, действия (скачать / внешний
 * аудит / удалить) и журнал внешних аудитов.
 *
 * Секция аудитов построена на useListAuditSessionsQuery с тем же тегом
 * `AuditSession:{scope}:{entity_id}`, что и модалка. rtkBridge инвалидирует
 * этот тег на WS-событие `audit_session_updated`, поэтому статусы сессий
 * (parsing → ready/failed/confirmed) обновляются в реальном времени без F5
 * и без поллинга. Уход со страницы / закрытие модалки НЕ прерывают фоновую
 * джобу — сессия живёт в БД, при возврате восстанавливается из этого списка.
 */

const Meta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(1)};

  & > b {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
`;

const MetaDivider = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Section = styled.section`
  margin-top: ${({ theme }) => theme.spacing(6)};

  & > h2 {
    margin: 0 0 ${({ theme }) => theme.spacing(3)};
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const SourceLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-weight: 500;
  font-size: 14px;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.radius.xs};
  }
`;

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 13px;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const SessionList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const SessionRow = styled.li`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  font-size: 13px;
`;

const SessionDate = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
`;

const SessionModel = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SessionSummary = styled.div`
  flex-basis: 100%;
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Spacer = styled.span`
  flex: 1;
`;

/** Бейдж режима сессии: аудит (нейтральный) / эталон (info). */
const KindBadge = styled.span<{ $kind: 'audit' | 'reference' }>`
  display: inline-block;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-radius: ${({ theme }) => theme.radius.pill};
  color: ${({ theme, $kind }) =>
    $kind === 'reference' ? theme.colors.status.info : theme.colors.text.secondary};
  background: ${({ theme, $kind }) =>
    $kind === 'reference' ? `${theme.colors.status.info}1A` : theme.colors.surface};
  border: 1px solid
    ${({ theme, $kind }) =>
      $kind === 'reference' ? `${theme.colors.status.info}33` : theme.colors.border};
`;

const dotPulse = keyframes`
  0%, 80%, 100% { opacity: 0.25; }
  40%           { opacity: 1; }
`;

/** Маленький анимированный индикатор «идёт парсинг» — три мигающие точки.
 *  При prefers-reduced-motion точки статичны (видно как «…»). */
const ParsingDots = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;

  & > i {
    width: 5px;
    height: 5px;
    border-radius: 999px;
    background: currentColor;
    animation: ${dotPulse} 1.2s ${({ theme }) => theme.motion.easeInOut} infinite;
  }
  & > i:nth-child(2) {
    animation-delay: 0.15s;
  }
  & > i:nth-child(3) {
    animation-delay: 0.3s;
  }

  @media (prefers-reduced-motion: reduce) {
    & > i {
      animation: none;
      opacity: 0.6;
    }
  }
`;

const StatusText = styled.span<{ $tone: 'info' | 'success' | 'danger' | 'muted' }>`
  font-weight: 600;
  color: ${({ theme, $tone }) =>
    $tone === 'success'
      ? theme.colors.status.success
      : $tone === 'danger'
        ? theme.colors.status.danger
        : $tone === 'info'
          ? theme.colors.status.info
          : theme.colors.text.muted};
`;

const SESSION_KIND_LABELS: Record<'audit' | 'reference', string> = {
  audit: 'аудит',
  reference: 'эталон',
};

function SessionStatus({ session }: { session: AuditSession }) {
  switch (session.status) {
    case 'parsing':
      return (
        <ParsingDots aria-label="Парсинг">
          Парсинг<i />
          <i />
          <i />
        </ParsingDots>
      );
    case 'ready':
      return <StatusText $tone="info">Готов к подтверждению</StatusText>;
    case 'confirmed':
      return (
        <StatusText $tone="success">
          Сохранён в память
          {session.confirmed_count > 0 ? ` · ${session.confirmed_count}` : ''}
        </StatusText>
      );
    case 'failed':
      return (
        <StatusText $tone="danger">
          Ошибка{session.error_message ? ` · ${session.error_message}` : ''}
        </StatusText>
      );
    default:
      return <StatusText $tone="muted">{session.status}</StatusText>;
  }
}

function DocumentBody({ composedId }: { composedId: string }) {
  const navigate = useNavigate();
  const { can_write } = useAgentPermission('statut');
  const accessToken = useAppSelector((s) => s.auth.accessToken);

  const { data, isLoading, isError } = useGetComposedDocQuery(composedId);

  // Тот же тег AuditSession:{scope}:{id}, что и в модалке → WS-инвалидация
  // даёт realtime-обновление статусов без поллинга. Скипаем пока scope неизвестен.
  const sessionsQuery = useListAuditSessionsQuery(
    data ? { scope: data.scope, id: composedId } : { scope: 'protocol_disagreement', id: composedId },
    { skip: !data },
  );

  const [auditOpen, setAuditOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deleteComposed, { isLoading: isDeleting }] = useDeleteComposedMutation();

  const back = () => navigate('/agents/statut/documents');

  if (isLoading) {
    return (
      <CardShell title="Документ" onBack={back} backLabel="К архиву">
        <Card>
          <Skeleton $height={28} $width="40%" />
          <div style={{ height: 12 }} />
          <Skeleton $height={16} $width="70%" />
          <div style={{ height: 24 }} />
          <Skeleton $height={160} />
        </Card>
      </CardShell>
    );
  }

  if (isError || !data) {
    return (
      <CardShell title="Документ не найден" onBack={back} backLabel="К архиву">
        <Card>
          <Muted>
            Запись могла быть удалена или ссылка устарела. Вернитесь в архив документов.
          </Muted>
          <Actions>
            <Button type="button" onClick={back}>
              К архиву документов
            </Button>
          </Actions>
        </Card>
      </CardShell>
    );
  }

  const { item, scope, related } = data;
  const badge = KIND_BADGE[scope] ?? { label: scope, tone: 'info' as const };
  const title = composedKindLabel(scope);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadAuthorizedFile(
        `/api/v1/agents/statut/composed/${item.id}/download`,
        { accessToken, fallbackName: `${title}-${item.id}.docx` },
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Удалить этот сгенерированный DOCX? Действие необратимо.')) {
      return;
    }
    await deleteComposed(item.id).unwrap();
    // WS-event composed_deleted инвалидирует архив; уводим юриста туда.
    navigate('/agents/statut/documents');
  };

  const sessions = sessionsQuery.data?.items ?? [];

  return (
    <CardShell
      title={title}
      onBack={back}
      backLabel="К архиву"
      actions={<Badge $tone={badge.tone}>{badge.label}</Badge>}
    >
      <Card>
        <Meta>
          <MetaItem>{formatComposedDt(item.created_at)}</MetaItem>
          <MetaDivider>·</MetaDivider>
          <MetaItem>{formatSize(item.size_bytes)}</MetaItem>
          {item.llm_model && (
            <>
              <MetaDivider>·</MetaDivider>
              <MetaItem>
                модель <b>{item.llm_model}</b>
              </MetaItem>
            </>
          )}
          {item.source_similarity != null && (
            <>
              <MetaDivider>·</MetaDivider>
              <MetaItem>
                похожесть <b>{(item.source_similarity * 100).toFixed(0)}%</b>
              </MetaItem>
            </>
          )}
        </Meta>

        <Section>
          <h2>Источник</h2>
          {related.contract ? (
            <SourceLink to={`/agents/statut/contracts/${related.contract.id}`}>
              Договор · {related.contract.counterparty}
              {related.contract.number ? ` · ${related.contract.number}` : ''}
            </SourceLink>
          ) : related.claim ? (
            <SourceLink to={`/agents/statut/claims/${related.claim.id}`}>
              Претензия · {related.claim.counterparty}
            </SourceLink>
          ) : related.lawsuit ? (
            <SourceLink to={`/agents/statut/lawsuits/${related.lawsuit.id}`}>
              {related.lawsuit.title}
            </SourceLink>
          ) : (
            <Muted>Источник не привязан</Muted>
          )}

          <Actions>
            <Button type="button" onClick={handleDownload} disabled={downloading}>
              {downloading ? 'Готовим…' : 'Скачать DOCX'}
            </Button>
            <Button type="button" $variant="secondary" onClick={() => setAuditOpen(true)}>
              Внешний аудит
            </Button>
            <Button
              type="button"
              $variant="ghost"
              onClick={() => void handleDelete()}
              disabled={!can_write || isDeleting}
              title={!can_write ? 'Только чтение' : 'Удалить этот DOCX'}
            >
              {isDeleting ? 'Удаляем…' : 'Удалить'}
            </Button>
          </Actions>
        </Section>

        <Section>
          <h2>Внешние аудиты</h2>
          {sessionsQuery.isLoading ? (
            <Skeleton $height={64} />
          ) : sessions.length === 0 ? (
            <Muted>Аудитов ещё не было.</Muted>
          ) : (
            <SessionList>
              {sessions.map((s) => (
                <SessionRow key={s.id}>
                  <SessionDate>{formatComposedDt(s.created_at)}</SessionDate>
                  <KindBadge $kind={s.kind ?? 'audit'}>
                    {SESSION_KIND_LABELS[s.kind ?? 'audit']}
                  </KindBadge>
                  <SessionModel>{s.source_model}</SessionModel>
                  <Spacer />
                  <SessionStatus session={s} />
                  <Button
                    type="button"
                    $size="sm"
                    $variant="ghost"
                    onClick={() => setAuditOpen(true)}
                  >
                    Открыть аудит
                  </Button>
                  {s.parsed_summary && <SessionSummary>{s.parsed_summary}</SessionSummary>}
                </SessionRow>
              ))}
            </SessionList>
          )}
        </Section>
      </Card>

      <ExternalAuditModal
        open={auditOpen}
        onClose={() => setAuditOpen(false)}
        scope={scope}
        entityId={item.id}
        entityLabel={title}
        allowReference
      />
    </CardShell>
  );
}

export function DocumentPage() {
  const { composedId } = useParams<{ composedId: string }>();

  if (!composedId) {
    return <Navigate to="/agents/statut/documents" replace />;
  }

  return <DocumentBody composedId={composedId} />;
}
